// frontend/src/site/templates/TemplateCorretorLovable/orchestrator.jsx
import React from 'react';

// ✅ Entry principal do template Lovable
import SiteCorretorLovable from './pages/SiteCorretorLovable';
import NotFound from './pages/NotFound';

/**
 * Orquestrador do Template Corretor (Lovable)
 * - Recebe o "ctx" do SiteHomeRouter (theme/estrutura/conteudo/perfilSite/ownerWhatsUrl/handle/properties...)
 * - Normaliza e entrega props para o entry (SiteCorretorLovable)
 *
 * ✅ Update:
 * - Suporta /u/:handle e /site/:slug (basePath automático)
 * - Expõe routing.basePath e também basePath (compat)
 * - Passa properties/loadingProps
 * - Mantém __raw com ctx inteiro para debug/compat
 */

const safeStr = (v) => (v == null ? '' : String(v));
const safeObj = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) ? v : {};
const safeArr = (v) => (Array.isArray(v) ? v : []);

function buildDisplayName(perfilSite = {}) {
  return (
    perfilSite?.nome ||
    perfilSite?.displayName ||
    perfilSite?.nomeCompleto ||
    perfilSite?.razaoSocial ||
    'Corretor'
  );
}

/**
 * Decide basePath para links internos do template.
 * - Se veio via /u/:handle => /u/{handle}
 * - Se veio via /site/:slug => /site/{slug}
 * - Se não souber: prioriza /u/{handle|slug}
 */
function resolveBasePath(ctx = {}) {
  const pathname = safeStr(ctx?.pathname || '').toLowerCase();
  const key = safeStr(ctx?.handle || ctx?.slug || '')
    .trim()
    .toLowerCase();

  if (pathname.startsWith('/site/')) return key ? `/site/${key}` : '/site';
  if (pathname.startsWith('/u/')) return key ? `/u/${key}` : '/u';

  // fallback seguro (prioriza /u)
  return key ? `/u/${key}` : '/u';
}

function normalizeFromContext(ctx = {}) {
  const theme = safeObj(ctx?.theme);

  // ✅ no seu builder atual, estrutura costuma ser ARRAY (seções)
  const estruturaRaw = ctx?.estrutura;
  const conteudo = safeObj(ctx?.conteudo);
  const perfilSite = safeObj(ctx?.perfilSite);

  const handle = safeStr(ctx?.handle || ctx?.slug || '')
    .trim()
    .toLowerCase();
  const slug = safeStr(ctx?.slug || ctx?.handle || '')
    .trim()
    .toLowerCase();

  const ownerWhatsUrl = safeStr(ctx?.ownerWhatsUrl || '');

  const properties = safeArr(ctx?.properties || []);
  const loadingProps = Boolean(ctx?.loadingProps);

  const basePath = resolveBasePath({
    pathname: ctx?.pathname,
    handle,
    slug,
  });

  // ✅ Perfil do dono
  const broker = {
    nome: buildDisplayName(perfilSite),
    headline: safeStr(
      perfilSite?.headline ||
        perfilSite?.cargo ||
        perfilSite?.especialidade ||
        conteudo?.hero?.frase ||
        ''
    ),
    bio: safeStr(
      conteudo?.sobre?.bio || perfilSite?.bio || perfilSite?.sobre || ''
    ),
    fotoUrl: safeStr(
      perfilSite?.fotoPerfilUrl ||
        perfilSite?.fotoUrl ||
        perfilSite?.photoURL ||
        perfilSite?.avatarUrl ||
        ''
    ),
    cidade: safeStr(perfilSite?.cidade || ''),
    estado: safeStr(perfilSite?.estado || ''),
    creci: safeStr(perfilSite?.creci || ''),
    contatos: {
      whatsappUrl: safeStr(
        ownerWhatsUrl || perfilSite?.whatsappUrl || perfilSite?.whatsUrl || ''
      ),
      whatsappDisplay: safeStr(
        perfilSite?.whatsappDisplay ||
          perfilSite?.perfil?.whatsappDisplay ||
          perfilSite?.telefone ||
          ''
      ),
      telefone: safeStr(perfilSite?.telefone || ''),
      email: safeStr(perfilSite?.email || ''),
      instagram: safeStr(
        perfilSite?.instagramUrl || perfilSite?.instagram || ''
      ),
    },
  };

  // ✅ Conteúdo do builder (mantém raw e também compat)
  const content = {
    // raw por seções
    hero: safeObj(conteudo?.hero),
    sobre: safeObj(conteudo?.sobre),
    imoveis: safeObj(conteudo?.imoveis),
    servicos: safeObj(conteudo?.servicos),
    provaSocial: safeObj(conteudo?.provaSocial),
    ctaFinal: safeObj(conteudo?.ctaFinal),
    rodape: safeObj(conteudo?.rodape),

    // compat (caso o template use chaves “antigas”)
    heroTitle: safeStr(conteudo?.heroTitle || conteudo?.titulo || ''),
    heroSubtitle: safeStr(conteudo?.heroSubtitle || conteudo?.subtitulo || ''),
    aboutTitle: safeStr(conteudo?.aboutTitle || ''),
    aboutText: safeStr(conteudo?.aboutText || ''),
    services: safeArr(conteudo?.services || conteudo?.servicos?.itens || []),
    testimonials: safeArr(
      conteudo?.testimonials || conteudo?.provaSocial?.depoimentos || []
    ),
  };

  // ✅ Estrutura (se vier array, deriva flags por id; se vier obj, usa showX)
  const estruturaArr = Array.isArray(estruturaRaw) ? estruturaRaw : null;

  const structure = {
    showHero: true,
    showAbout: true,
    showServices: true,
    showProperties: true,
    showTestimonials: true,
    showCTA: true,
    showFooter: true,
    sections: estruturaArr || [],
  };

  if (estruturaArr) {
    const byId = new Map(
      estruturaArr
        .filter(Boolean)
        .map((s) => [safeStr(s?.id).trim(), s?.ativo !== false])
    );

    const isOn = (id) => byId.get(id) !== false;

    structure.showHero = isOn('hero');
    structure.showAbout = isOn('sobre');
    structure.showServices = isOn('servicos');
    structure.showProperties = isOn('imoveis') || isOn('destaques');
    structure.showTestimonials = isOn('provaSocial') || isOn('depoimentos');
    structure.showCTA = isOn('ctaFinal');
    structure.showFooter = isOn('rodape');
  } else {
    const e = safeObj(estruturaRaw);
    structure.showHero = e?.showHero ?? true;
    structure.showAbout = e?.showAbout ?? true;
    structure.showServices = e?.showServices ?? true;
    structure.showProperties = e?.showProperties ?? true;
    structure.showTestimonials = e?.showTestimonials ?? true;
    structure.showCTA = e?.showCTA ?? true;
    structure.showFooter = e?.showFooter ?? true;
  }

  const themeNormalized = {
    ...theme,
    primary: safeStr(theme?.primary || theme?.primaryColor || ''),
    background: safeStr(theme?.background || ''),
    text: safeStr(theme?.text || ''),
    logoUrl: safeStr(theme?.logoUrl || ''),
    cssVars: safeObj(theme?.cssVars || {}),
    _key: safeStr(theme?._key || ''),
  };

  return {
    // ✅ compat antigo
    slug,
    handle,
    basePath,

    // ✅ NOVO padrão recomendado pro Lovable
    routing: {
      slug,
      handle,
      basePath,
    },

    theme: themeNormalized,
    estrutura: structure,
    conteudo: content,
    perfil: broker,

    properties,
    loadingProps,

    __raw: {
      ...safeObj(ctx),
      theme,
      estrutura: estruturaRaw,
      conteudo,
      perfilSite,
      ownerWhatsUrl,
    },
  };
}

export const TemplateCorretorLovableOrchestrator = {
  id: 'corretor_lovable',
  label: 'Template Corretor (Lovable)',
  version: '1.2.0',

  render({ page = 'home', ctx, slug }) {
    const mergedCtx = {
      ...safeObj(ctx),
      // ✅ garante slug/handle se vier separado
      slug: slug || ctx?.slug || ctx?.handle || '',
    };

    const data = normalizeFromContext(mergedCtx);

    if (page === 'notfound') return <NotFound />;

    return <SiteCorretorLovable {...data} />;
  },
};
